/**
 * El punto donde la marca activa se convierte en pixeles.
 *
 * Dos mecanismos tienen que moverse juntos, y por eso viven acá y no en dos
 * lugares distintos:
 *
 *   1. `ConfigProvider theme={themeFor(id)}` — repinta las primitivas de AntD.
 *   2. La clase en `<body>` — activa los overrides `--silia-*` que lee el CSS
 *      escrito a mano. Sin esto solo repinta AntD y el CSS propio se queda en
 *      la marca anterior, que es peor que no cambiar nada.
 *
 * Ver `spec.md` §4 (H1).
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { ConfigProvider } from 'antd';
import type { ConfigProviderProps } from 'antd';
import { bodyClassFor, themeFor, TEMPLATE_LIST, DEFAULT_TEMPLATE } from './index';
import type { TemplateId } from './index';
import type { BrandResolver } from './BrandResolver';
import { BRAND_META } from './brands/meta';

type BrandContextValue = {
  /** Marca activa. Nunca undefined: cae a DEFAULT_TEMPLATE. */
  brandId: TemplateId;
  /** Cambia la marca en runtime. No persiste — eso lo decide el resolver. */
  setBrandId: (id: TemplateId) => void;
  /** true mientras un resolver asíncrono todavía no respondió. */
  isResolving: boolean;
};

const BrandContext = createContext<BrandContextValue | null>(null);

export const useBrand = (): BrandContextValue => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand debe usarse dentro de <BrandProvider>');
  return ctx;
};

export type BrandProviderProps = {
  /**
   * De dónde sale la marca. Ver `spec.md` §6 (decidida: por deploy, ADR-044);
   * `index.tsx` pasa `envDefaultResolver`. Si se omite, la app corre con
   * `DEFAULT_TEMPLATE`.
   */
  resolver?: BrandResolver;
  /**
   * Props que se le pasan al ConfigProvider además del tema.
   *
   * `ConfigProviderProps` viene del propio componente para no quedar desfasado
   * en un upgrade de antd; `theme` se excluye porque lo maneja este provider.
   */
  configProviderProps?: Omit<ConfigProviderProps, 'theme' | 'children'>;
  children: React.ReactNode;
};

export const BrandProvider: React.FC<BrandProviderProps> = ({
  resolver,
  configProviderProps,
  children,
}) => {
  const [brandId, setBrandId] = useState<TemplateId>(
    () => resolver?.resolveSync?.() ?? DEFAULT_TEMPLATE,
  );
  const [isResolving, setIsResolving] = useState(Boolean(resolver?.resolve));

  // El inicializador de useState corre UNA sola vez. Sin esto, un resolver que
  // cambia de identidad —o que empieza a devolver otra marca— dejaría `brandId`
  // congelado en el valor del primer render, y como el efecto de la clase
  // depende de `brandId`, el <body> se quedaría con la marca anterior.
  useEffect(() => {
    const next = resolver?.resolveSync?.();
    if (next) setBrandId(next);
  }, [resolver]);

  useEffect(() => {
    if (!resolver?.resolve) return;
    let cancelled = false;
    resolver
      .resolve()
      .then((id) => {
        if (!cancelled && id) setBrandId(id);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolver]);

  // La clase en <body> es lo que activa los overrides --silia-* del CSS escrito
  // a mano. Se limpian TODAS las clases de marca antes de poner la nueva: si
  // solo se agregara, dos marcas seguidas dejarían los dos bloques activos y
  // ganaría el que esté más abajo en el stylesheet.
  //
  // useLayoutEffect y no useEffect: corre antes de que el navegador pinte. Con
  // useEffect, un deploy de otra marca mostraba un frame con los tokens de
  // Silia de :root antes de que llegara la clase.
  useLayoutEffect(() => {
    const classes = TEMPLATE_LIST.map((t) => t.bodyClass).filter(Boolean);
    document.body.classList.remove(...classes);
    const next = bodyClassFor(brandId);
    if (next) document.body.classList.add(next);
    return () => {
      if (next) document.body.classList.remove(next);
    };
  }, [brandId]);

  // El título y el favicon de `index.html` los escribe el build (ADR-044). Con la
  // marca elegida en runtime (Silia OS) la pestaña tiene que seguirla también.
  useEffect(() => {
    const meta = BRAND_META[brandId];
    document.title = meta.title;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) icon.href = `${process.env.PUBLIC_URL ?? ''}${meta.favicon}`;
  }, [brandId]);

  const themeConfig = useMemo(() => themeFor(brandId), [brandId]);
  const value = useMemo(() => ({ brandId, setBrandId, isResolving }), [brandId, isResolving]);

  // Los toasts estáticos de AntD (`message.success(...)`, `notification`) se pintan
  // fuera de este árbol. Con `cssVar` sus estilos leen variables que solo existen
  // dentro del ConfigProvider: sin esto salían con fondo transparente y texto
  // negro. `holderRender` los monta bajo el mismo tema.
  useEffect(() => {
    ConfigProvider.config({
      holderRender: (holder) => (
        <ConfigProvider {...configProviderProps} theme={themeConfig}>
          {holder}
        </ConfigProvider>
      ),
    });
  }, [configProviderProps, themeConfig]);

  return (
    <BrandContext.Provider value={value}>
      <ConfigProvider {...configProviderProps} theme={themeConfig}>
        {children}
      </ConfigProvider>
    </BrandContext.Provider>
  );
};
